<template>
    <main-layout>
        <template #header>
            <main-header :title="'게시물 작성' | translate"/>
        </template>
        <template #body>
            <main-body>
                <div class="d-flex mt-2">
                    <div class="ml-3">
                      <!-- 공지사항 타입 셀렉트 박스 -->
                      <search-single-select-box
                          :items="countryOptions"
                          :label="'국가코드' | translate"
                          @update:selected="onFilterCountryCodesChanged"
                          style="width: 150px;"
                          :value="countryCode"
                      />
                    </div>
                    <div class="ml-3">
                        <!-- 공지사항 타입 셀렉트 박스 -->
                        <search-single-select-box
                            :items="noticeTypeOptions"
                            :label="'공지사항 타입' | translate"
                            @update:selected="onFilterNoticeTypeChanged"
                            style="width: 150px;"
                            :value="boardTypeId"
                        />
                    </div>
                    <div class="ml-3">
                        <!-- 공지사항 상세 여부 셀렉트 박스 -->
                        <search-single-select-box
                            :items="noticeDetailOptions"
                            :label="'상세여부' | translate"
                            @update:selected="onFilterNoticeDetailChanged"
                            style="width: 150px;"
                            :value="noticeDetailYn"
                        />
                    </div>
                    <admin-date-time-picker
                        :title="'게시' | translate"
                        :startDate.sync="startDate"
                        :startTime.sync="startTime"
                        :endDate.sync="endDate"
                        :endTime.sync="endTime"
                    />
                </div>
                <v-text-field
                    class="mt-5"
                    :placeholder="'제목' | translate"
                    outlined
                    dense
                    v-model="title"
                    ref="title"
                />
                <quill-editor
                    ref="editor"
                    :options="options"
                    v-model="content"
                    style="width: 100%; height: calc(100% - 250px); background-color: #ffffff;"
                />
            </main-body>
        </template>
        <template #footer>
            <div class="d-flex">
                <v-btn @click="onSaveClick">
                    <v-icon>
                        mdi-content-save-outline
                    </v-icon>
                </v-btn>
            </div>
        </template>
    </main-layout>
</template>

<script>
import MainHeader from "@/components/common/layout/MainHeader";
import MainLayout from "@/components/common/layout/MainLayout";
import MainBody from "@/components/common/layout/MainBody";
import SearchSingleSelectBox from "@/components/input/SearchSingleSelectBox";
import AdminDateTimePicker from "@/components/input/AdminDateTimePicker";
import {DateTime} from "luxon";
import {PATH} from '@/constants'
import TotalCodeOptions from "@/assets/options/TotalCodeOptions";

export default {
    name: "BoardEdit",
    components: {
        MainBody,
        MainLayout,
        MainHeader,
        SearchSingleSelectBox,
        AdminDateTimePicker,
    },
    mixins: [TotalCodeOptions],
    created() {
        // 공지사항 옵션 로드
        this.loadCountryOptions();
        this.loadNoticeTypeOptions()
        this.loadNoticeDetailOptions()
        // 파라미터로 넘겨받은 값이 있는지 확인
        const raw = this.$route?.params?.raw

        if (typeof raw === "undefined") {
            // 기본 시간은 오늘로부터 1주일을 지정한다.
            const defaultStartDateTime = DateTime.local().plus({minutes: 15})
            const defaultEndDateTime = DateTime.local().plus({weeks: 1, minutes: 15})
            const startDate = defaultStartDateTime.toISODate()
            const startTime = defaultStartDateTime.toFormat('HH:mm')
            const endDate = defaultEndDateTime.toISODate()
            const endTime = defaultEndDateTime.toFormat('HH:mm')
            this.startDate = startDate
            this.startTime = startTime
            this.endDate = endDate
            this.endTime = endTime
        } else {
            this.boardId = raw.boardId
            this.countryCode = raw.countryCode
            this.boardTypeId = raw.boardTypeId
            this.noticeDetailYn = raw.noticeDetailYn
            this.title = raw.title
            this.content = raw.content
            // 시간 처리
            const startDate = DateTime.fromSQL(raw.noticeStartDatetime).plus({hours: 9}).toISODate()
            const endDate = DateTime.fromSQL(raw.noticeEndDatetime).plus({hours: 9}).toISODate()
            const startTime = DateTime.fromSQL(raw.noticeStartDatetime).plus({hours: 9}).toFormat('HH:mm')
            const endTime = DateTime.fromSQL(raw.noticeEndDatetime).plus({hours: 9}).toFormat('HH:mm')
            this.startDate = startDate
            this.startTime = startTime
            this.endDate = endDate
            this.endTime = endTime
        }
    },
    data() {
        return {
            noticeTypeOptions: [],
            noticeDetailOptions: [],
            max: null,
            min: null,
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            boardId: null,
            countryCode: null,
            boardTypeId: null,
            noticeDetailYn: null,
            title: '',
            content: '',
            options: {
                modules: {
                    toolbar: {
                        container: [
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ 'header': 1 }, { 'header': 2 }],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'script': 'sub'}, { 'script': 'super' }],
                            [{ 'indent': '-1'}, { 'indent': '+1' }],
                            [{ 'direction': 'rtl' }],
                            [{ 'size': ['small', false, 'large', 'huge'] }],
                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'font': [] }],
                            [{ 'align': [] }],
                            ['clean'],
                            ['image'],
                        ],
                    },
                    imageDrop: true,
                    imageResize: {
                        modules: [ 'Resize', 'DisplaySize' ]
                    },
                }
            },
        }
    },
    methods: {
        onFilterNoticeTypeChanged(boardTypeId) {
            this.boardTypeId = boardTypeId
        },
        onFilterCountryCodesChanged(countryCode) {
            this.countryCode = countryCode
        },
        onFilterNoticeDetailChanged(noticeDetailYn) {
            this.noticeDetailYn = noticeDetailYn
        },
        onSaveClick() {
            // 국가 코드 설정 체크
            const countryCode = this.countryCode
            if (countryCode === null || countryCode === undefined) {
              alert(this.t('국가코드를 선택해주세요.'))
              return
            }
            // 공지사항 타입 설정 체크
            const boardTypeId = this.boardTypeId
            if (boardTypeId === null || boardTypeId === undefined) {
                alert(this.t('공지사항 타입을 선택해주세요.'))
                return
            }
            // 상세설정 설정 체크
            const noticeDetailYn = this.noticeDetailYn
            if (noticeDetailYn === null || noticeDetailYn === undefined) {
                alert(this.t('상세설정 여부를 선택해주세요.'))
                return
            }
            // 날짜 설정 체크
            const startDateTime = DateTime.fromSQL(`${this.startDate} ${this.startTime}`).toString()
            const endDateTime = DateTime.fromSQL(`${this.endDate} ${this.endTime}`).toString()
            const startDateTimeValue = new Date(startDateTime).getTime()
            const endDateTimeValue = new Date(endDateTime).getTime()
            if (startDateTimeValue >= endDateTimeValue) {
                alert(this.t("날짜를 다시 지정해주세요."))
                return
            }
            // 제목 체크
            const title = this.title.trim()
            if (!title) {
                alert(this.t("제목을 입력하여 주세요."))
                this.$refs.title.focus()
                return
            }
            // 내용 체크
            const content = this.content
            if (!content) {
                alert(this.t("내용을 입력하여 주세요."))
                return
            }
            // 저장 및 수정
            if (confirm(this.t('이대로 저장하시겠습니까?'))) {
                this.requestNoticeUpdate({
                  boardTypeId,
                  noticeDetailYn,
                  title,
                  content,
                  startDateTime,
                  endDateTime,
                })
            }
        },
        async requestNoticeUpdate(data) {
            try {
                const displayName = this.$userManager.user.displayName
                console.log("#DISPLAY NAME : ", displayName)
                // TODO : 수정 API 추가 필요
                await this.$request(this.boardId ? PATH.BOARD.NOTICE.UPDATE : PATH.BOARD.NOTICE.ADD)
                    .setObject({
                        boardId: this.boardId,
                        countryCode: this.countryCode,
                        boardTypeId: data.boardTypeId,
                        noticeDetailYn: data.noticeDetailYn,
                        title: data.title,
                        content: data.content,
                        regUserId: displayName,
                        uptUserId: displayName,
                        noticeStartDatetime: data.startDateTime,
                        noticeEndDatetime: data.endDateTime,
                        useYn: 'Y',
                    })
                    .enqueue()
                alert(this.t("게시물이 저장 되었습니다."))
                this.$router.push({name: "managertnbox.board.list"})
            } catch (error) {
                console.error(error)
            }
        }
    }
}
</script>

<style scoped>
</style>
